import * as React from "react";

import { cn } from "./cn";
import { MonthMark } from "./period-timeline";

type MonthChipStatus =
  "reconciled" | "auto_reconciled" | "open" | "future" | "before_books";

type MonthChipItem = {
  key: string;
  label: string;
  status?: MonthChipStatus;
  disabled?: boolean;
};

const STATUS_WORD: Record<MonthChipStatus, string> = {
  reconciled: "reconciled",
  auto_reconciled: "auto-reconciled",
  open: "open",
  future: "not yet available",
  before_books: "before your books start",
};

/**
 * The month strip's marks, so a month reads the same everywhere: a check when
 * reconciled (by hand or automatically), an amber dot when open, a ring for
 * the running period, nothing otherwise.
 */
function ChipMark({
  status,
  isCurrent,
}: {
  status: MonthChipStatus | undefined;
  isCurrent: boolean;
}) {
  if (isCurrent) return <MonthMark status="current" />;
  if (status === "reconciled" || status === "auto_reconciled")
    return <MonthMark status="complete" />;
  if (status === "open") return <MonthMark status="open" />;
  return null;
}

type MonthChipsProps = {
  ariaLabel: string;
  items: MonthChipItem[];
  /** The running period, set apart after the strip ("October – Now"). */
  current?: { key: string; label: string } | null;
  value: string | null;
  onChange: (key: string) => void;
  size?: "default" | "sm";
  className?: string;
};

type Entry = MonthChipItem & { isCurrent: boolean };

/**
 * One joined strip of month chips (a radiogroup). Arrow keys move and select;
 * disabled chips are skipped. The strip scrolls inside itself on a phone.
 */
function MonthChips({
  ariaLabel,
  items,
  current = null,
  value,
  onChange,
  size = "default",
  className,
}: MonthChipsProps) {
  const refs = React.useRef(new Map<string, HTMLButtonElement>());
  const entries: Entry[] = [
    ...items.map((item) => ({
      ...item,
      disabled:
        item.disabled ||
        item.status === "future" ||
        item.status === "before_books",
      isCurrent: false,
    })),
    ...(current ? [{ ...current, isCurrent: true }] : []),
  ];
  const enabled = entries.filter((entry) => !entry.disabled);
  const tabKey = enabled.some((entry) => entry.key === value)
    ? value
    : enabled[0]?.key;

  React.useEffect(() => {
    if (value == null) return;
    refs.current
      .get(value)
      ?.scrollIntoView?.({ inline: "nearest", block: "nearest" });
  }, [value]);

  const move = (from: string, to: Entry | undefined) => {
    if (!to || to.key === from) return;
    onChange(to.key);
    refs.current.get(to.key)?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent, key: string) => {
    const index = enabled.findIndex((entry) => entry.key === key);
    let target: Entry | undefined;
    if (event.key === "ArrowRight" || event.key === "ArrowDown")
      target = enabled[Math.min(index + 1, enabled.length - 1)];
    else if (event.key === "ArrowLeft" || event.key === "ArrowUp")
      target = enabled[Math.max(index - 1, 0)];
    else if (event.key === "Home") target = enabled[0];
    else if (event.key === "End") target = enabled[enabled.length - 1];
    else return;
    event.preventDefault();
    if (target) move(key, target);
  };

  const small = size === "sm";
  const lastItem = items.length - 1;

  return (
    <div
      data-slot="month-chips"
      className={cn(
        "max-w-full overflow-x-auto py-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        className,
      )}
    >
      <div
        role="radiogroup"
        aria-label={ariaLabel}
        className="flex w-max items-center"
      >
        {entries.map((entry, index) => {
          const selected = entry.key === value;
          const tinted =
            entry.status === "reconciled" || entry.status === "auto_reconciled";
          const word = entry.status ? STATUS_WORD[entry.status] : null;
          const first = index === 0;
          const endOfStrip = !entry.isCurrent && index === lastItem;
          return (
            <button
              key={entry.key}
              ref={(el) => {
                if (el) refs.current.set(entry.key, el);
                else refs.current.delete(entry.key);
              }}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={
                entry.isCurrent || !word
                  ? entry.label
                  : `${entry.label}, ${word}`
              }
              disabled={entry.disabled}
              tabIndex={entry.key === tabKey ? 0 : -1}
              onClick={() => onChange(entry.key)}
              onKeyDown={(event) => onKeyDown(event, entry.key)}
              className={cn(
                "relative inline-flex shrink-0 items-center justify-center gap-1.5 border border-border px-3 font-medium whitespace-nowrap focus-visible:z-20 focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-offset-1 focus-visible:outline-ring disabled:cursor-not-allowed",
                small ? "h-8 text-compact" : "h-[38px] text-[15px]",
                entry.isCurrent
                  ? "ml-1 rounded-control bg-muted text-foreground"
                  : cn(
                      "-ml-px",
                      first && "ml-0 rounded-l-control",
                      endOfStrip && "rounded-r-control",
                    ),
                !entry.isCurrent &&
                  (tinted
                    ? "bg-success-tint text-success"
                    : entry.disabled
                      ? "bg-card text-muted-foreground opacity-60"
                      : "bg-card text-body hover:bg-muted"),
                selected &&
                  (tinted || entry.isCurrent
                    ? "z-10 ring-1 ring-success"
                    : "z-10 ring-1 ring-primary"),
              )}
            >
              {entry.label}
              <ChipMark status={entry.status} isCurrent={entry.isCurrent} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

export { MonthChips };
export type { MonthChipItem, MonthChipsProps, MonthChipStatus };
